import { z } from 'zod';
import { isValidCron } from './cron.js';
import { EMAIL_TEMPLATE_KEYS, EmailTemplateText, OPS_JOBS } from './email.js';
/**
 * A client profile (clients/<name>/profile.json). Seeds the `settings` table on first deploy;
 * afterwards the values in `settings` are authoritative and editable by Admin.
 */
const nonEmpty = z.string().trim().min(1);
/** A PNG/JPEG file name inside the client's assets folder (no paths). */
const assetFile = z.union([z.literal(''), z.string().regex(/^[A-Za-z0-9_-][A-Za-z0-9_.-]*\.(png|jpe?g)$/i, 'must be a .png/.jpg file name in assets/')]);
const uniqueList = (label) => z
    .array(nonEmpty)
    .min(1)
    .refine((xs) => new Set(xs).size === xs.length, `${label} must not contain duplicates`);
export const ClientProfile = z
    .object({
    company: z.object({
        name: nonEmpty,
        shortName: nonEmpty,
        tagline: z.string().default(''),
        addressLine: nonEmpty,
        phone: nonEmpty,
        fax: z.string().default(''),
    }),
    /** Image file names in clients/<name>/assets for the form PDFs. Empty = print the company name. */
    branding: z
        .object({
        logo: assetFile.default(''),
        lubeBadge: assetFile.default(''),
    })
        .strict()
        .default({}),
    appName: nonEmpty,
    timezone: nonEmpty,
    workOrders: z.object({
        firstNumber: z.number().int().positive(),
        legacyMaxNumber: z.number().int().nonnegative().default(0),
    }),
    jobTypes: uniqueList('jobTypes'),
    billingTypes: z
        .array(z.object({ code: nonEmpty, label: nonEmpty }))
        .min(1)
        .refine((xs) => new Set(xs.map((x) => x.code)).size === xs.length, 'billingTypes codes must be unique'),
    holdReasons: uniqueList('holdReasons'),
    certificationStatement: z.object({
        version: z.number().int().positive(),
        text: nonEmpty,
    }),
    lube: z.object({
        items: uniqueList('lube.items'),
        lubricants: uniqueList('lube.lubricants'),
        brandName: z.string().default(''),
        /**
         * Which lube items are filters, and the words that identify a matching Parts List line
         * (case-insensitive, in the description or part number). Drives "Filter changed, no part".
         */
        filterParts: z.record(nonEmpty, z.array(nonEmpty).min(1)).default({}),
    }),
    rental: z.object({
        controls: uniqueList('rental.controls'),
        inspectionItems: uniqueList('rental.inspectionItems'),
        requiredPhotoSlots: uniqueList('rental.requiredPhotoSlots'),
        /** Fuel / DEF gauge marks, emptiest first; "used" at check-in is counted in these steps. */
        levels: uniqueList('rental.levels').default(['E', '1/8', '1/4', '3/8', '1/2', '5/8', '3/4', '7/8', 'F']),
        /** The photo slot showing the hour meter: submitting an inspection records the reading with it. */
        hourMeterSlot: z.string().trim().min(1).optional(),
    }),
    voice: z.object({
        /** Off until Ryan approves Vertex spend; transcription falls back to typing. */
        enabled: z.boolean().default(false),
        modelId: nonEmpty,
        location: nonEmpty,
        language: nonEmpty,
        maxSeconds: z.number().int().positive().max(600),
        dailyCapUsd: z.number().positive(),
        pricePerMillion: z.object({ audioInput: z.number().nonnegative(), textInput: z.number().nonnegative(), output: z.number().nonnegative() }),
        glossary: z.array(nonEmpty),
    }),
    closeRules: z.object({
        laborRequiredJobTypes: z.array(nonEmpty),
        lubeJobTypes: z.array(nonEmpty),
        rentalCheckInJobTypes: z.array(nonEmpty),
        rentalCheckOutJobTypes: z.array(nonEmpty),
    }),
    mechanic: z.object({
        idleLockMinutes: z.number().int().positive(),
        pinLength: z.number().int().min(4).max(10),
        maxFailedPins: z.number().int().positive(),
        lockoutMinutes: z.number().int().positive(),
        /** How long after an online PIN login a device may sync that mechanic's offline work. */
        offlineSyncDays: z.number().int().positive(),
    }),
    flags: z.object({
        unaccountedDayMinHours: z.number().positive(),
        unaccountedDayMaxHours: z.number().positive(),
        longPunchHours: z.number().positive(),
        hourMeterMaxPerDay: z.number().positive(),
        staleJobDays: z.number().int().positive(),
        longHoldDays: z.number().int().positive(),
        comebackDays: z.number().int().positive(),
        photoTimeMismatchMinutes: z.number().int().positive(),
        offlineDriftMinutes: z.number().int().nonnegative(),
        /** Job types the "Parts, no labor / Labor, no parts" flags apply to. */
        repairJobTypes: z.array(nonEmpty).default([]),
    }),
    email: z.object({
        dailyCap: z.number().int().positive(),
        dailyDigestCron: nonEmpty,
        weeklyIntegrityCron: nonEmpty,
        /** Display name on the From line. The address is SENDER_EMAIL in client.env. */
        senderName: z.string().default(''),
        /** Service manager address(es). Empty: every active Manager with an email. Also the Reply-To. */
        serviceManagerEmails: z.array(z.string().email()).default([]),
        /** Owner address(es) for infrastructure results and the weekly report. Empty: ALERT_EMAIL. */
        ownerEmails: z.array(z.string().email()).default([]),
        /** Minutes to wait before each retry; its length is the number of retries (then alert). */
        retryMinutes: z.array(z.number().int().positive()).min(1).default([2, 10, 30]),
        /** How long a customer-email preview token stays valid. */
        previewMinutes: z.number().int().positive().max(1440).default(30),
        /** A scheduled email missed (VM down) is still sent if this many minutes late or less. */
        scheduleCatchUpMinutes: z.number().int().nonnegative().max(1440).default(180),
        /** Infrastructure jobs whose success is emailed too. Failures are always emailed. */
        opsSuccessEmails: z.array(z.enum(OPS_JOBS)).default(['restore']),
        templates: z.object(Object.fromEntries(EMAIL_TEMPLATE_KEYS.map((k) => [k, EmailTemplateText]))),
    }),
})
    .strict()
    .refine((p) => p.workOrders.firstNumber > p.workOrders.legacyMaxNumber, {
    message: 'workOrders.firstNumber must be above legacyMaxNumber',
    path: ['workOrders', 'firstNumber'],
})
    .refine((p) => Object.values(p.closeRules).flat().every((t) => p.jobTypes.includes(t)), {
    message: 'closeRules may only name job types listed in jobTypes',
    path: ['closeRules'],
})
    .refine((p) => !p.rental.hourMeterSlot || p.rental.requiredPhotoSlots.includes(p.rental.hourMeterSlot), {
    message: 'rental.hourMeterSlot must be one of rental.requiredPhotoSlots',
    path: ['rental', 'hourMeterSlot'],
})
    .refine((p) => p.flags.repairJobTypes.every((t) => p.jobTypes.includes(t)), {
    message: 'flags.repairJobTypes may only name job types listed in jobTypes',
    path: ['flags', 'repairJobTypes'],
})
    .refine((p) => Object.keys(p.lube.filterParts).every((i) => p.lube.items.includes(i)), {
    message: 'lube.filterParts may only name items listed in lube.items',
    path: ['lube', 'filterParts'],
})
    .refine((p) => isValidCron(p.email.dailyDigestCron) && isValidCron(p.email.weeklyIntegrityCron), {
    message: 'email cron strings must be valid 5-field cron expressions',
    path: ['email'],
})
    .refine((p) => p.flags.unaccountedDayMinHours < p.flags.unaccountedDayMaxHours, {
    message: 'unaccountedDayMinHours must be below unaccountedDayMaxHours',
    path: ['flags'],
});
//# sourceMappingURL=profile.js.map